import React, { useState, useEffect } from 'react';
import style from './style.module.css';

import { useParams } from 'react-router-dom';
import countries from "i18n-iso-countries";

import { useNotification } from '../../utils/notifications';

import { 
    getLanguageShortname, Language, 
    getLanguageArray, systemLanguage, getLabel,
    usersLabel, getOrganizations,
    getActiveOrganization, Organization,
    activeOrganization,
    getUuid, getToken, 
    shortname,
    urlUser, languagesList,
    parseUserLanguage
} from '../../utils/constants';

import { CreateUser, GetUser, EditUser } from '../../services/userApi';

import Loader from '../../assets/images/loader.svg';


const UserView: React.FC = () => {

    const uuid = getUuid()
    const token = getToken()

    const notify = useNotification()

    const { user_uuid, action } = useParams();

    countries.registerLocale(require("i18n-iso-countries/langs/en.json"));

    const countryObj = countries.getNames("en", { select: "official" });

    const [sessionLang, setSessionLang] = useState<Language>(getLanguageArray());

    const [userLanguage, setUserLanguage] = useState<{ short: string | any } | false>(false);
    const user_language = getLanguageShortname();

    const [activeOrganizationName, setActiveOrganizationName] = useState<string | undefined>(undefined);
    const [activeOrganizationId,setActiveOrganizationId] = useState<number | undefined>(0)
    const [usersLoading,setUsersLoading] = useState<boolean>(true)
    const [viewType,setViewType] = useState<string>('create')
    const [isEditing,setIsEditing] = useState<boolean>(false)

    const [userFirstName, setUserFirstName] = useState<string>('')
    const [userLastName, setUserLastName] = useState<string>('')
    const [userJobTitle, setUserJobTitle] = useState<string>('')
    const [userEmail, setUserEmail] = useState<string>('')
    const [userPhone, setUserPhone] = useState<string>('')
    const [userPassword, setUserPassword] = useState<string>('')
    const [userCountry, setUserCountry] = useState<string>('')
    const [userAddress, setUserAddress] = useState<string>('')
    const [userPostal, setUserPostal] = useState<string>('')
    const [userCreateLanguage,setUserCreateLanguage] = useState<string>('')
    const [userEditSelectLanguage,setUserEditSelectLanguage] = useState<string>('')
    const [userLimited,setUserLimited] = useState<boolean>(false)

    const [isSaving,setIsSaving] = useState<boolean>(false)

    const handleEditUser = () => {
        setIsEditing(true)
    }
    
    const handleLanguageSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.currentTarget.value
        setUserCreateLanguage(value)
        setUserEditSelectLanguage(value)
    }

    const handleSaveUser = async () => {
        setIsEditing(false)
        setIsSaving(true)

        const editUserAccount = await EditUser({
            uuid: uuid,
            token: token, 
            user_uuid: user_uuid,
            firstname: userFirstName, 
            lastname: userLastName, 
            password: userPassword, 
            phone: userPhone, 
            country: userCountry, 
            address_street_city: userAddress,
            postal_code: userPostal, 
            job_title: userJobTitle,
            language: userCreateLanguage
        })

        notify('Saved Successfully', 3000)
        setIsSaving(false)
    }

    const handleCreateUser = async () => {

        setIsSaving(true)

        const createUserAccount = await CreateUser({
            uuid: uuid,
            token: token, 
            firstname: userFirstName, 
            lastname: userLastName, 
            email: userEmail,
            password: userPassword, 
            phone: userPhone, 
            country: userCountry, 
            address_street_city: userAddress,
            postal_code: userPostal, 
            job_title: userJobTitle, 
            organization_id: activeOrganizationId, 
            limited: userLimited,
            language: userCreateLanguage
        })

        
        setUserFirstName("")
        setUserLastName("")
        setUserJobTitle("")
        setUserEmail("")
        setUserCountry("")
        setUserAddress("")
        setUserPostal("")
        setUserPhone("")
        setUserAddress("")
        setUserLimited(false)

        notify('User Created', 3000)
        setIsSaving(false)
    
    }

    const renderLanguage = () => (
        <>
            {
                Object.entries(languagesList).map(([key, lang]) => (
                    <option value={lang.short} key={key}>{lang.name}</option>
                ))
            }
        </>
    )

    const handleActiveOrganizationChange = async () => {
        const activeOrg = getActiveOrganization(); 
        const organizationsList: Organization[] = getOrganizations();
        const activeId = activeOrg?.id;
        const idToFind = typeof activeId === 'number' ? activeId : (organizationsList[0]?.id);
        if (typeof idToFind !== 'number') {
            setActiveOrganizationName(undefined);
            return;
        }
        const selectedOrg = organizationsList.find((org: Organization) => org.id === idToFind);
        setActiveOrganizationName(selectedOrg?.name ?? undefined);
        setActiveOrganizationId(activeId)
    }

    

    const handleGetUserInfo = async() => {
        const userInfo = await GetUser({uuid: uuid, token: token, user_uuid: user_uuid})

        setUserFirstName(userInfo.firstname)
        setUserLastName(userInfo.lastname)
        setUserJobTitle(userInfo.job_title)
        setUserEmail(userInfo.email)
        setUserCountry(userInfo.country)
        setUserAddress(userInfo.address)
        setUserPostal(userInfo.postal_code)
        setUserPhone(userInfo.phone)
        setUserAddress(userInfo.address_street_city)

        const userLanguageDefines = parseUserLanguage(userInfo.settings) as Language;
        const userLanguageDefinesSelect = parseUserLanguage(userInfo.settings,true) as string;
        setUserCreateLanguage(userLanguageDefines.name)
        setUserEditSelectLanguage(userLanguageDefinesSelect)

        setUsersLoading(false)

    }

    useEffect(() => {
        if(user_uuid !== 'create')
        {
            setViewType('edit')
            if(action === 'edit')
            {
                setIsEditing(true)
            }
            handleGetUserInfo()
            return
        }
        setIsEditing(true)
        setUsersLoading(false)
    }, [user_uuid])

    useEffect(() => {
        if (activeOrganizationName) return;
        handleActiveOrganizationChange()
    }, [activeOrganizationName]);

    useEffect(() => {
        if (user_language) {
            setUserLanguage({ short: user_language.toUpperCase() });
        } else {
            setUserLanguage({ short: ((user_language as unknown) as Language).short || 'EN' });
        }
    }, [user_language]);

    useEffect(() => {
        window.scrollTo(0,0);
    }, []);

    useEffect(() => {
        const handler = (e: StorageEvent) => {
            if (e.key === systemLanguage && e.newValue) {
                setSessionLang(JSON.parse(e.newValue));
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, []);

    useEffect(() => {
        const handler = (e: StorageEvent) => {
            if (e.key === activeOrganization && e.newValue) {
                handleActiveOrganizationChange()
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, []);

    return (
        <div className={style.scoreboard}>
            <div className={style.header}>
                <div className={style.headerLeft}>
                    <div className={`${style.greeting} ${style.textContainerGrad}`}>
                        {/*getLabel(userLabel, sessionLang)*/}
                        {viewType === 'create' ? 'Create User' : usersLoading ? 'Loading...' : userFirstName + ' ' + userLastName}
                    </div>
                    <div className={style.info}>
                        {usersLoading ? '' : activeOrganizationName}
                    </div>
                </div><div className={style.headerRight}>
                    {isEditing ? 
                        isSaving ? 
                            <img src={Loader} /> : 
                            <span className={style.save} onClick={viewType === 'create' ? handleCreateUser : handleSaveUser}>{viewType === 'create' ? 'Create' : 'Save' }</span> : 
                            <span onClick={handleEditUser}>Edit User</span>
                    }
                </div>
            </div>
            {
                !usersLoading ? 
                <>
                <div className={style.tableWrap}>
                    <div className={`${style.tableRow} ${style.header}`}>
                        <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.name}`}>
                            General Info
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>First Name</div>
                                <div className={style.inputText}>{isEditing ? <input type="text" value={userFirstName} onChange={(e) => setUserFirstName(e.currentTarget.value)} /> : userFirstName}</div>
                            </div>
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Last Name</div>
                                <div className={style.inputText}>{isEditing ? <input type="text" value={userLastName} onChange={(e) => setUserLastName(e.currentTarget.value)} /> : userLastName}</div>
                            </div>
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Language</div>
                                <div className={style.inputText}>
                                    {
                                        isEditing ?
                                            <select value={userEditSelectLanguage} onChange={handleLanguageSelect}>
                                                <option value="">Select</option>
                                                {renderLanguage()}
                                            </select> : userCreateLanguage
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Job Title</div>
                                <div className={style.inputText}>{isEditing ? <input type="text" value={userJobTitle} onChange={(e) => setUserJobTitle(e.currentTarget.value)} /> : userJobTitle}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`${style.tableWrap} ${style.tableTopSpace}`}>
                    <div className={`${style.tableRow} ${style.header}`}>
                        <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.name}`}>
                            Login Information
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Email</div>
                                <div className={style.inputText}>{isEditing ? <input type="text" value={userEmail} onChange={(e) => setUserEmail(e.currentTarget.value)} disabled={viewType === 'create' ? false : true} /> : userEmail}</div>
                            </div>
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Password</div>
                                <div className={style.inputText}>{isEditing ? <input type="password" value={userPassword} placeholder={viewType !== 'create' ? 'Leave Blank Unless You Want to Change it' : ''} onChange={(e) => setUserPassword(e.currentTarget.value)} /> : '********'}</div>
                            </div>
                        </div>
                    </div>
                    {
                        viewType === 'create' ? 
                            <div className={`${style.tableRow}`}>
                                <div className={style.input}>
                                    <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                        <div className={style.inputLeft}>Access</div>
                                        <div className={style.inputText}>
                                             <select onChange={(e) => setUserLimited(e.target.value === 'true' ? true : false)}>
                                                <option value="">Select</option>
                                                <option value={'false'} key={'full'}>Academy & Simulations</option>
                                                <option value={'true'} key={'limited'}>Simulations Only</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        : ''
                    }
                </div>

                <div className={`${style.tableWrap} ${style.tableTopSpace}`}>
                    <div className={`${style.tableRow} ${style.header}`}>
                        <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.name}`}>
                            Contact Information
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Phone</div>
                                <div className={style.inputText}>{isEditing ? <input type="text" value={userPhone} onChange={(e) => setUserPhone(e.currentTarget.value)} /> : userPhone}</div>
                            </div>
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Country</div>
                                <div className={style.inputText}>{isEditing ? 
                                    <select value={userCountry} onChange={(e) => setUserCountry(e.target.value)}>
                                        <option value="">Select</option>
                                        {Object.entries(countryObj).map(([code, name]) => (
                                            <option key={code} value={code}>{name}</option>
                                        ))}
                                        </select>
                                : userCountry}</div>
                            </div>
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Address</div>
                                <div className={style.inputText}>{isEditing ? <input type="text" value={userAddress} onChange={(e) => setUserAddress(e.currentTarget.value)} /> : userAddress}</div>
                            </div>
                        </div>
                    </div>
                    <div className={`${style.tableRow}`}>
                        <div className={style.input}>
                            <div className={`${style.tableCol} ${style.shortname}`}></div><div className={`${style.tableCol} ${style.input}`}>
                                <div className={style.inputLeft}>Postal Code</div>
                                <div className={style.inputText}>{isEditing ? <input type="text" value={userPostal} onChange={(e) => setUserPostal(e.currentTarget.value)} /> : userPostal}</div>
                            </div>
                        </div>
                    </div>
                </div>
                </> : ''
            }
        </div>
    );
}

export default UserView;
